import { useState } from "react";
import { Plus } from "lucide-react";
import { cn } from "../../lib/utils";

export interface AccordionItemProps {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}

/**
 * FAQ editorial: itens separados só por hairlines, um aberto por vez,
 * com transição de altura suave via grid-template-rows (0fr → 1fr).
 */
export function Accordion({ items }: { items: AccordionItemProps[] }) {
  const [open, setOpen] = useState<number | null>(items[0]?.defaultOpen ? 0 : null);

  return (
    <div className="border-t border-line">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={item.question} className="border-b border-line">
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
              className="group flex w-full items-baseline gap-4 py-5 text-left sm:gap-6 sm:py-6"
            >
              <span className="font-mono text-xs text-subtle tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span
                className={cn(
                  "flex-1 font-display text-lg font-medium tracking-tight transition-colors sm:text-xl",
                  isOpen ? "text-accent" : "text-foreground group-hover:text-accent",
                )}
              >
                {item.question}
              </span>
              <span
                className={cn(
                  "grid size-7 shrink-0 translate-y-1 place-items-center rounded-full border transition-all duration-300",
                  isOpen
                    ? "rotate-45 border-accent/40 bg-accent-soft text-accent"
                    : "border-line text-subtle group-hover:border-accent/30 group-hover:text-accent",
                )}
              >
                <Plus className="size-4" strokeWidth={2} />
              </span>
            </button>
            <div
              className="grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
              style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
            >
              <div className="overflow-hidden">
                <p className="max-w-2xl pb-6 pl-8 text-sm leading-relaxed text-muted-foreground sm:pl-12 sm:text-[15px]">
                  {item.answer}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
